/**
 * EditGroupModal — управление группой:
 *   - Цвет фона аватарки (визуальный, persist в localStorage per-topic)
 *   - Название
 *   - Аватар (фото upload + emoji + удалить)
 *   - Управление участниками (добавить/удалить, назначить/снять админа)
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  editChatTopic, uploadTopicAvatar, deleteTopicAvatar, topicAvatarUrl,
  fetchTopicMembers, addTopicMembers, removeTopicMember, setTopicMemberRole,
  fetchActiveUsers, userAvatarUrl, fetchTaxonomy,
  type ChatTopic, type ChatTopicMember, type UserShort,
} from "@/api/dashboard";
import { TOPIC_ORGS_KEY, setTopicOrg } from "@/api/orgs";
import { useTopicOrgs } from "@/lib/topicOrgs";
import { Icon } from "@/components/prototype/Icon";
import { PACK_ICON_COUNT, packIconUrl, parseTopicIcon } from "@/data/animatedIcons";

const COLORS = [
  { id: "green",  hex: "#4fae4e" },
  { id: "blue",   hex: "#3390ec" },
  { id: "orange", hex: "#e99a12" },
  { id: "purple", hex: "#9c7ce5" },
  { id: "red",    hex: "#e85c5c" },
  { id: "teal",   hex: "#00acc1" },
];

function colorForKey(key: string): string {
  const hash = (key || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return COLORS[hash % COLORS.length].hex;
}

function initialsFor(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function isEmojiIcon(icon: string | null | undefined): boolean {
  if (!icon) return false;
  return !icon.startsWith("svg:") && !icon.startsWith("lottie:") && !icon.startsWith("lottie_url:") && !icon.startsWith("http");
}

export function EditGroupModal({ topic, onClose, myUserId }: {
  topic: ChatTopic;
  onClose: () => void;
  myUserId: string | null;
}) {
  const qc = useQueryClient();

  const [name, setName] = useState(topic.name);
  const [department, setDepartment] = useState<string>(topic.department ?? "");
  // Организация группы (04.10.2026): пометка в списке и шапке. Сменить можно, только если все участники
  // группы состоят в новой организации (иначе сервер ответит, кто мешает).
  const topicOrgs = useTopicOrgs();
  const curTopicOrg = topicOrgs.orgOf(topic);
  const [orgSel, setOrgSel] = useState<number | null>(null);
  const canPickOrg = topic.kind === "private_group" && topicOrgs.multi;
  const [emoji, setEmoji] = useState<string>(isEmojiIcon(topic.icon) ? topic.icon || "" : "");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>(
    topic.avatar_filename ? (topicAvatarUrl(topic.id, topic.avatar_filename) || "") : ""
  );
  const [removeAvatar, setRemoveAvatar] = useState(false);
  // Цвет — persist per-topic в localStorage
  const colorKey = `mng-topic-color-${topic.id}`;
  const [color, setColor] = useState<string>(() => {
    try {
      const v = localStorage.getItem(colorKey);
      if (v && COLORS.some(c => c.id === v)) return v;
    } catch { /* ignore */ }
    // дефолт по hash от имени
    const def = COLORS[(topic.name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];
    return def.id;
  });
  const colorHex = COLORS.find(c => c.id === color)?.hex || colorForKey(name);

  // Members + active users
  const membersQ = useQuery<ChatTopicMember[]>({
    queryKey: ["topic-members-edit", topic.id],
    queryFn: () => fetchTopicMembers(topic.id),
    staleTime: 30_000,
  });
  const usersQ = useQuery({
    queryKey: ["users-active"],
    queryFn: fetchActiveUsers,
    staleTime: 5 * 60_000,
  });
  // Список отделов из БД (taxonomy_departments) — динамический, чтобы новые
  // отделы появлялись здесь без правки кода / деплоя.
  const taxonomyQ = useQuery({
    queryKey: ["taxonomy"],
    queryFn: fetchTaxonomy,
    staleTime: 5 * 60_000,
  });

  const [memberSearch, setMemberSearch] = useState("");
  const [showAddPicker, setShowAddPicker] = useState(false);

  const members = membersQ.data ?? [];
  const memberIds = useMemo(() => new Set(members.map(m => m.user_id)), [members]);
  const curOrgId = curTopicOrg?.id ?? null;
  const candidates = useMemo(() => {
    // В группу организации — только её участники (сервер всё равно не пустит постороннего)
    const inOrg = (u: UserShort) => !topicOrgs.multi || curOrgId == null
      || ((u as UserShort & { org_ids?: number[] }).org_ids ?? []).includes(curOrgId);
    const all = (usersQ.data ?? []).filter(u => u.id !== myUserId && !memberIds.has(u.id) && inOrg(u));
    if (!memberSearch.trim()) return all;
    const q = memberSearch.trim().toLowerCase();
    return all.filter(u => (u.display_name ?? "").toLowerCase().includes(q));
  }, [usersQ.data, memberIds, memberSearch, myUserId, topicOrgs.multi, curOrgId]);

  const addMembersMut = useMutation({
    mutationFn: (uids: string[]) => addTopicMembers(topic.id, uids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["topic-members-edit", topic.id] });
      qc.invalidateQueries({ queryKey: ["topic-members-drawer", topic.id] });
    },
    onError: (e: Error) => alert(`Не удалось добавить: ${e.message}`),
  });
  const removeMemberMut = useMutation({
    mutationFn: (uid: string) => removeTopicMember(topic.id, uid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["topic-members-edit", topic.id] });
      qc.invalidateQueries({ queryKey: ["topic-members-drawer", topic.id] });
    },
    onError: (e: Error) => alert(`Не удалось удалить: ${e.message}`),
  });
  const roleMut = useMutation({
    mutationFn: (vars: { uid: string; role: "member" | "admin" }) =>
      setTopicMemberRole(topic.id, vars.uid, vars.role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["topic-members-edit", topic.id] });
      qc.invalidateQueries({ queryKey: ["topic-members-drawer", topic.id] });
    },
    onError: (e: Error) => alert(`Не удалось изменить роль: ${e.message}`),
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      // 0. Организация — первой: если кто-то из участников в ней не состоит, сервер откажет и ничего не сохраним
      if (canPickOrg && orgSel != null && orgSel !== curOrgId) {
        await setTopicOrg(topic.id, orgSel);
      }
      // 1. Patch имя + emoji icon
      await editChatTopic(topic.id, {
        name: name.trim(),
        icon: emoji || null,
        department: department || "",
      });
      // 2. Color → localStorage
      try { localStorage.setItem(colorKey, color); } catch { /* ignore */ }
      // 3. Avatar: либо удалить, либо загрузить новый
      if (removeAvatar && topic.avatar_filename && !avatarFile) {
        try { await deleteTopicAvatar(topic.id); } catch (e) { console.error(e); }
      }
      if (avatarFile) {
        try { await uploadTopicAvatar(topic.id, avatarFile); } catch (e) { console.error(e); }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat/topics"] });
      qc.invalidateQueries({ queryKey: TOPIC_ORGS_KEY });
      onClose();
    },
    onError: (e: Error) => alert(`Не удалось сохранить: ${e.message}`),
  });

  const showRemove = (avatarPreview && !avatarFile) || avatarFile;

  return (
    <div className="mng-modal-overlay show" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mng-modal" onClick={(e) => e.stopPropagation()}>
        <header className="mng-modal-header">
          <span className="mng-modal-title">Управление группой</span>
          <button className="mng-modal-close" onClick={onClose} title="Закрыть">
            <Icon name="x" size={16}/>
          </button>
        </header>
        <div className="mng-modal-body">

          {/* === Avatar === */}
          <div className="mng-modal-group">
            <div className="mng-avatar-upload">
              {avatarPreview && !removeAvatar ? (
                <img className="mng-avatar mng-avatar-lg" src={avatarPreview} alt="" style={{ objectFit: "cover" }}/>
              ) : (() => {
                const parsed = parseTopicIcon(emoji);
                if (parsed.kind === "pack") {
                  return <img className="mng-avatar mng-avatar-lg" src={parsed.url} alt=""
                    style={{ objectFit: "contain", background: "transparent" }}/>;
                }
                return (
                  <div
                    className="mng-avatar mng-avatar-lg"
                    style={{ background: colorHex, fontSize: emoji ? 36 : undefined }}
                  >
                    {emoji || initialsFor(name)}
                  </div>
                );
              })()}
              <div className="mng-avatar-hint">
                <label className="mng-avatar-upload-btn">
                  Загрузить фото
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setAvatarFile(f);
                      setRemoveAvatar(false);
                      setEmoji("");
                      const reader = new FileReader();
                      reader.onload = () => setAvatarPreview(typeof reader.result === "string" ? reader.result : "");
                      reader.readAsDataURL(f);
                    }}
                  />
                </label>
                {showRemove && (
                  <button
                    type="button"
                    className="mng-avatar-remove"
                    onClick={() => {
                      setAvatarFile(null);
                      setAvatarPreview("");
                      setRemoveAvatar(true);
                    }}
                  >Убрать</button>
                )}
              </div>
            </div>
          </div>

          {/* === Color === */}
          <div className="mng-modal-group">
            <label className="mng-modal-label">Цвет фона</label>
            <div className="mng-folder-colors">
              {COLORS.map(c => (
                <button
                  key={c.id}
                  type="button"
                  className={`mng-folder-color-opt${color === c.id ? " active" : ""}`}
                  onClick={() => setColor(c.id)}
                  style={{ background: c.hex, color: c.hex }}
                  title={c.id}
                />
              ))}
            </div>
          </div>

          {/* === Name === */}
          <div className="mng-modal-group">
            <label className="mng-modal-label">Название группы</label>
            <input
              type="text"
              className="mng-modal-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* === Department === */}
          <div className="mng-modal-group">
            <label className="mng-modal-label">Отдел</label>
            <select
              className="mng-modal-input"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              style={{ cursor: "pointer" }}
            >
              <option value="">— без этикетки —</option>
              {(taxonomyQ.data?.departments ?? []).map(d => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
              {/* Fallback на статический список если taxonomy ещё не загружен */}
              {!taxonomyQ.data && (<>
                <option value="sales">Отдел продаж</option>
                <option value="production">Производство</option>
                <option value="marketing">Маркетинг</option>
                <option value="admin">Администрация</option>
                <option value="finance">Финансы</option>
                <option value="supply">Отдел снабжения</option>
              </>)}
            </select>
          </div>

          {/* === Организация (04.10.2026) === */}
          {canPickOrg && (
            <div className="mng-modal-group">
              <label className="mng-modal-label">Организация</label>
              <select className="mng-modal-input" value={orgSel ?? curOrgId ?? ""} style={{ cursor: "pointer" }}
                aria-label="Организация" onChange={(e) => setOrgSel(Number(e.target.value))}>
                {curOrgId == null && <option value="" disabled>— без организации —</option>}
                {topicOrgs.mine.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <div className="org-pick-hint">Группу, переписку и файлы видят только участники выбранной организации. Сменить можно, если все участники группы в ней состоят.</div>
            </div>
          )}

          {/* === Иконка из пака (100 шт., 3D-emoji) === */}
          <div className="mng-modal-group">
            <label className="mng-modal-label">Иконка (если нет фото)</label>
            <div
              className="mng-emoji-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(48px, 1fr))",
                gap: 6,
                maxHeight: 280,
                overflowY: "auto",
              }}
            >
              {Array.from({ length: PACK_ICON_COUNT }, (_, i) => i + 1).map(n => {
                const value = `pack:${n}`;
                const parsed = parseTopicIcon(emoji);
                const isActive = parsed.kind === "pack" && parsed.index === n;
                return (
                  <button
                    key={n}
                    type="button"
                    title={`Иконка #${n}`}
                    className={`mng-emoji-opt${isActive ? " active" : ""}`}
                    onClick={() => {
                      if (isActive) setEmoji("");
                      else {
                        setEmoji(value);
                        if (avatarFile) { setAvatarFile(null); setAvatarPreview(""); setRemoveAvatar(true); }
                      }
                    }}
                    style={{ aspectRatio: "1 / 1", padding: 3 }}
                  >
                    <img
                      src={packIconUrl(n)}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      width={40}
                      height={40}
                      style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* === Members === */}
          <div className="mng-modal-group">
            <label className="mng-modal-label">
              Участники ({members.length})
              <button
                type="button"
                className="mng-btn-link"
                onClick={() => setShowAddPicker(v => !v)}
                style={{ marginLeft: 8 }}
              >{showAddPicker ? "Скрыть" : "+ Добавить"}</button>
            </label>

            {/* Picker — список доступных юзеров */}
            {showAddPicker && (
              <>
                <div className="mng-search" style={{ margin: "8px 0" }}>
                  <Icon name="search" size={16}/>
                  <input
                    type="text"
                    placeholder="Поиск пользователей..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                  />
                </div>
                <div className="mng-user-list" style={{ maxHeight: 200 }}>
                  {candidates.length === 0 && <div className="mng-modal-empty">Все доступные юзеры уже в группе</div>}
                  {candidates.map(u => {
                    const url = userAvatarUrl(u.id, u.avatar_filename);
                    return (
                      <div
                        key={u.id}
                        className="mng-user-list-item"
                        onClick={() => { addMembersMut.mutate([u.id]); }}
                      >
                        {url ? (
                          <img className="mng-avatar" src={url} alt=""/>
                        ) : (
                          <div className="mng-avatar" style={{ background: colorForKey(u.id) }}>
                            {initialsFor(u.display_name || "?")}
                          </div>
                        )}
                        <div className="mng-list-info">
                          <div className="mng-list-name">{u.display_name || "(без имени)"}</div>
                          <div className="mng-list-status">{u.position || ""}</div>
                        </div>
                        <Icon name="plus" size={16}/>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Текущие участники */}
            <div className="mng-user-list">
              {members.length === 0 && <div className="mng-modal-empty">Нет участников</div>}
              {members.map(m => {
                const url = userAvatarUrl(m.user_id, m.avatar_filename);
                const isAdmin = m.role === "admin";
                const isMe = m.user_id === myUserId;
                return (
                  <div key={m.user_id} className="mng-user-list-item">
                    {url ? (
                      <img className="mng-avatar" src={url} alt=""/>
                    ) : (
                      <div className="mng-avatar" style={{ background: colorForKey(m.user_id) }}>
                        {initialsFor(m.display_name || "?")}
                      </div>
                    )}
                    <div className="mng-list-info">
                      <div className="mng-list-name">
                        {m.display_name || "?"}
                        {isMe && <span style={{ color: "#707579", fontSize: 12, marginLeft: 6 }}>(вы)</span>}
                      </div>
                      <div className="mng-list-status">{isAdmin ? "Модератор" : "Участник"}</div>
                    </div>
                    <button
                      type="button"
                      className={`mng-admin-toggle${isAdmin ? " active" : ""}`}
                      onClick={() => roleMut.mutate({ uid: m.user_id, role: isAdmin ? "member" : "admin" })}
                      disabled={isMe}
                      title={isMe ? "Свою роль изменить нельзя" : (isAdmin ? "Снять админа" : "Назначить админом")}
                    >Админ</button>
                    {!isMe && (
                      <button
                        type="button"
                        className="mng-member-remove"
                        onClick={() => {
                          if (window.confirm(`Удалить ${m.display_name} из группы?`)) {
                            removeMemberMut.mutate(m.user_id);
                          }
                        }}
                        title="Удалить из группы"
                      >
                        <Icon name="x" size={14}/>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

        </div>
        <div className="mng-modal-footer">
          <button className="mng-btn mng-btn-secondary" onClick={onClose}>Отмена</button>
          <button
            className="mng-btn mng-btn-primary"
            disabled={!name.trim() || saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >{saveMut.isPending ? "Сохранение…" : "Сохранить"}</button>
        </div>
      </div>
    </div>
  );
}
